import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, BookOpen, Clock, Video, Edit, X, CreditCard, Link as LinkIcon, MessageSquare, FileText, Package, ChevronRight, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatDateTime12Hour, formatDateTimeForInput, parseTimeForBackend } from '../utils/timeFormatting';
import TimeInput12Hour from '../components/TimeInput12Hour';
import NoticeDisplay from '../components/NoticeDisplay';

/**
 * Tutor-only widget: shows Google Calendar / Meet connection status and
 * offers a one-click Connect / Disconnect. On first load, reads
 * ?google=... from the URL to show the outcome of an OAuth round-trip.
 */
const GoogleCalendarButton: React.FC = () => {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const loadStatus = async () => {
    try {
      const res = await api.get('/google-calendar/status');
      setConfigured(!!res.data?.configured);
      setConnected(!!res.data?.connected);
      setConnectedEmail(res.data?.connectedEmail ?? null);
    } catch {
      setConfigured(false);
    }
  };

  useEffect(() => {
    loadStatus();
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('google');
    if (outcome) {
      const map: Record<string, string> = {
        connected: 'Google Calendar connected.',
        error: 'Google returned an error — try again.',
        missing: 'Google redirect was missing info — try again.',
        'bad-state': 'Session expired during Google sign-in — try again.',
        'no-refresh-token': 'Google did not return a refresh token — try again and make sure to approve access.',
        'exchange-failed': 'Could not finish Google sign-in — try again.',
      };
      setFlash(map[outcome] ?? `Google: ${outcome}`);
      // Remove the query param from the URL after reading it.
      const url = new URL(window.location.href);
      url.searchParams.delete('google');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await api.get('/google-calendar/authorize');
      if (res.data?.url) {
        window.location.assign(res.data.url);
      } else {
        setFlash('Could not start Google sign-in.');
        setBusy(false);
      }
    } catch (err: any) {
      setFlash(err?.response?.data?.message || 'Could not start Google sign-in.');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post('/google-calendar/disconnect');
      setConnected(false);
      setConnectedEmail(null);
      setFlash('Google Calendar disconnected.');
    } finally {
      setBusy(false);
    }
  };

  if (configured === null) return null;
  if (!configured) return null; // server env vars not set yet — hide the section

  return (
    <div className="w-full">
      {connected ? (
        <button
          onClick={disconnect}
          disabled={busy}
          className="w-full btn btn-outline"
        >
          {busy ? 'Disconnecting…' : `Disconnect Google Calendar${connectedEmail ? ` (${connectedEmail})` : ''}`}
        </button>
      ) : (
        <button
          onClick={connect}
          disabled={busy}
          className="w-full btn btn-primary"
        >
          {busy ? 'Opening Google…' : 'Connect Google Calendar'}
        </button>
      )}
      {flash && (
        <p className="mt-2 text-xs text-gray-600 text-center">{flash}</p>
      )}
    </div>
  );
};

// PaymentLinksAdmin moved to src/components/admin/PaymentLinksAdmin.tsx
// and now lives on Admin → Financials → Settings.

/**
 * Tutor-only button that manually triggers the 24h session reminder tick
 * via POST /api/reminders/run. Saves having to curl the endpoint by hand
 * while we're still testing email delivery.
 */
const RunReminderTickButton: React.FC = () => {
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    setStatus('Running…');
    try {
      const res = await api.post('/reminders/run');
      const d = res.data || {};
      const fmt = (p: any) => `c=${p?.considered ?? '?'} s=${p?.sent ?? '?'} f=${p?.failed ?? '?'}`;
      setStatus(`24h ${fmt(d.reminder24h)} · 30m ${fmt(d.join30min)} · pay ${fmt(d.paymentReminder)}`);
    } catch (err: any) {
      setStatus(`Failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="w-full">
      <button
        onClick={run}
        disabled={busy}
        className="w-full btn btn-outline"
      >
        {busy ? 'Running reminder tick…' : 'Run reminder tick (email test)'}
      </button>
      {status && (
        <p className="mt-2 text-xs text-gray-600 text-center">{status}</p>
      )}
    </div>
  );
};

interface Subject {
  id: string;
  name: string;
  category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
}

interface Booking {
  id: string;
  startTime: string;
  endTime: string;
  type: 'LESSON' | 'CONSULTATION';
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'REQUESTED' | 'AMENDED' | 'DENIED';
  price: number;
  isOnline: boolean;
  title?: string;
  subject: {
    name: string;
    onlineRate: number;
    inPersonRate: number;
  };
  student?: {
    id: string;
    name: string;
  };
  tutor?: {
    id: string;
    name: string;
  };
  meetingLink?: string;
  notes?: string;
}

const DashboardPage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [upcomingBookings, setUpcomingBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [teamsLink, setTeamsLink] = useState('');
  const [showAmendModal, setShowAmendModal] = useState(false);
  const [amendmentData, setAmendmentData] = useState({
    startTime: '',
    endTime: '',
    notes: ''
  });
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    feedback: '',
    stepsToImprove: '',
    homeworkTargets: ''
  });
  const [showSubjectSelector, setShowSubjectSelector] = useState(false);
  const [allSubjects, setAllSubjects] = useState<Subject[]>([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<string[]>([]);
  const [savingSubjects, setSavingSubjects] = useState(false);

  useEffect(() => {
    fetchUpcomingBookings();
    if (user?.role === 'STUDENT') {
      fetchAllSubjects();
      // Initialize selected subjects from user profile
      if (user?.studentProfile?.subjects) {
        setSelectedSubjectIds(user.studentProfile.subjects.map(s => s.id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllSubjects = async () => {
    try {
      const response = await api.get('/subjects');
      setAllSubjects(response.data);
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
    }
  };

  const updateSubjects = async () => {
    try {
      setSavingSubjects(true);
      await api.put('/users/profile', {
        subjects: selectedSubjectIds
      });

      // Properly refresh user data in the auth context
      await refreshUser();
      setShowSubjectSelector(false);
    } catch (error) {
      console.error('Failed to update subjects:', error);
      window.alert('Failed to update subjects. Please try again.');
    } finally {
      setSavingSubjects(false);
    }
  };

  const fetchUpcomingBookings = async () => {
    try {
      const response = await api.get(`/bookings/${user?.id}`);
      const now = new Date();
      
      // Ensure response.data is an array before filtering
      const bookingsData = Array.isArray(response.data) ? response.data : [];
      const upcoming = bookingsData.filter((booking: Booking) => 
        new Date(booking.endTime) > now && 
        ['PENDING', 'CONFIRMED', 'REQUESTED', 'AMENDED'].includes(booking.status)
      );
      setUpcomingBookings(upcoming.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    return formatDateTime12Hour(dateString);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED': return 'text-green-600 bg-green-100';
      case 'PENDING': return 'text-yellow-600 bg-yellow-100';
      case 'CANCELLED': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const isTutorSuggested = (booking: Booking) => {
    return booking.notes?.includes('[TUTOR_SUGGESTED]') || false;
  };

  const getTutorMessage = (booking: Booking) => {
    if (!booking.notes?.includes('[TUTOR_SUGGESTED]')) return null;
    // Extract message after [TUTOR_SUGGESTED] marker
    const message = booking.notes.replace('[TUTOR_SUGGESTED]', '').trim();
    return message || 'Lesson suggested by tutor';
  };

  const handleAmendBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    // Pre-fill with current booking time in 12-hour format
    const startTime = formatDateTimeForInput(booking.startTime);
    const endTime = formatDateTimeForInput(booking.endTime);
    setAmendmentData({
      startTime,
      endTime,
      notes: ''
    });
    setShowAmendModal(true);
  };

  const handleCancelBooking = async (bookingId: string) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      await api.put(`/bookings/${bookingId}/status`, { status: 'CANCELLED' });
      fetchUpcomingBookings(); // Refresh the list
      window.alert('Booking cancelled successfully');
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      window.alert('Failed to cancel booking');
    }
  };

  const handleAttachTeamsLink = (booking: Booking) => {
    setSelectedBooking(booking);
    setTeamsLink(booking.meetingLink || '');
    setShowTeamsModal(true);
  };

  const handleSaveTeamsLink = async () => {
    if (!selectedBooking) return;

    try {
      await api.put(`/bookings/${selectedBooking.id}/teams-link`, {
        meetingLink: teamsLink
      });
      
      // Update the booking in the local state
      setUpcomingBookings(prev => 
        prev.map(booking => 
          booking.id === selectedBooking.id 
            ? { ...booking, meetingLink: teamsLink }
            : booking
        )
      );
      
      setShowTeamsModal(false);
      setSelectedBooking(null);
      setTeamsLink('');
      window.alert('Teams link saved successfully!');
    } catch (error) {
      console.error('Failed to save Teams link:', error);
      window.alert('Failed to save Teams link. Please try again.');
    }
  };

  const handleCloseTeamsModal = () => {
    setShowTeamsModal(false);
    setSelectedBooking(null);
    setTeamsLink('');
  };

  const handleAmendSubmit = async () => {
    if (!selectedBooking) return;

    try {
      // Use different endpoint based on user role
      const endpoint = user?.role === 'TUTOR' 
        ? `/bookings/${selectedBooking.id}/tutor-amend`
        : `/bookings/${selectedBooking.id}/amend`;
        
      const response = await api.put(endpoint, {
        startTime: parseTimeForBackend(amendmentData.startTime),
        endTime: parseTimeForBackend(amendmentData.endTime),
        notes: amendmentData.notes
      });

      // Close modal first
      setShowAmendModal(false);
      setSelectedBooking(null);
      setAmendmentData({ startTime: '', endTime: '', notes: '' });
      
      // Refresh bookings to get latest status from backend
      await fetchUpcomingBookings();
      
      // Different messages for tutor vs student
      if (user?.role === 'TUTOR') {
        window.alert('Booking rescheduled successfully!');
      } else {
        window.alert('Booking amended successfully! The tutor will be notified.');
      }
    } catch (error: any) {
      console.error('Failed to amend booking:', error);
      window.alert(error.response?.data?.message || 'Failed to amend booking. Please try again.');
    }
  };

  const handleCloseAmendModal = () => {
    setShowAmendModal(false);
    setSelectedBooking(null);
    setAmendmentData({ startTime: '', endTime: '', notes: '' });
  };

  const handleAddFeedback = async (booking: Booking) => {
    // Check if feedback already exists
    try {
      const existingFeedback = await api.get(`/feedback/booking/${booking.id}`);
      if (existingFeedback.data) {
        // Pre-fill with existing feedback
        setFeedbackData({
          feedback: existingFeedback.data.feedback,
          stepsToImprove: existingFeedback.data.stepsToImprove,
          homeworkTargets: existingFeedback.data.homeworkTargets
        });
      } else {
        // Clear form for new feedback
        setFeedbackData({
          feedback: '',
          stepsToImprove: '',
          homeworkTargets: ''
        });
      }
    } catch (error) {
      // No existing feedback, start fresh
      setFeedbackData({
        feedback: '',
        stepsToImprove: '',
        homeworkTargets: ''
      });
    }
    
    setSelectedBooking(booking);
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = async () => {
    if (!selectedBooking) return;

    try {
      // Check if feedback already exists
      let existingFeedback = null;
      try {
        const response = await api.get(`/feedback/booking/${selectedBooking.id}`);
        existingFeedback = response.data;
      } catch (error) {
        // No existing feedback
      }

      if (existingFeedback) {
        // Update existing feedback
        await api.put(`/feedback/${existingFeedback.id}`, feedbackData);
        window.alert('Feedback updated successfully!');
      } else {
        // Create new feedback
        await api.post('/feedback', {
          bookingId: selectedBooking.id,
          ...feedbackData
        });
        window.alert('Feedback added successfully!');
      }

      setShowFeedbackModal(false);
      setSelectedBooking(null);
      setFeedbackData({ feedback: '', stepsToImprove: '', homeworkTargets: '' });
    } catch (error: any) {
      console.error('Failed to save feedback:', error);
      window.alert(error.response?.data?.message || 'Failed to save feedback. Please try again.');
    }
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
    setSelectedBooking(null);
    setFeedbackData({ feedback: '', stepsToImprove: '', homeworkTargets: '' });
  };

  const handleAcceptSuggestion = async (bookingId: string) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, { status: 'CONFIRMED' });
      window.alert('Lesson booking confirmed! You can now see the payment link.');
      await fetchUpcomingBookings();
    } catch (error: any) {
      console.error('Failed to accept suggestion:', error);
      window.alert(error.response?.data?.message || 'Failed to accept suggestion. Please try again.');
    }
  };

  const handleDeclineSuggestion = async (bookingId: string) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, { status: 'DENIED' });
      window.alert('Lesson suggestion declined.');
      await fetchUpcomingBookings();
    } catch (error: any) {
      console.error('Failed to decline suggestion:', error);
      window.alert(error.response?.data?.message || 'Failed to decline suggestion. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.name}!
        </h1>
        <p className="text-gray-600 mt-2">
          Here's what's happening with your {user?.role === 'STUDENT' ? 'learning' : 'tutoring'} today.
        </p>
      </div>

      {/* Quick Actions Section */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Quick Actions</h2>
          </div>
          <div className="space-y-3">
            {user?.role === 'STUDENT' ? (
              <>
                <button 
                  onClick={() => navigate('/schedule')}
                  className="w-full btn btn-primary"
                >
                  Book a New Lesson
                </button>
                <button 
                  onClick={() => navigate('/resources')}
                  className="w-full btn btn-outline"
                >
                  View Homework and Resources
                </button>
                <button 
                  onClick={() => navigate('/payments')}
                  className="w-full btn btn-secondary flex items-center justify-center space-x-2"
                >
                  <Package className="w-4 h-4" />
                  <span>Buy Hour Package</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/schedule')}
                  className="w-full btn btn-primary"
                >
                  Set Availability
                </button>
                <button
                  onClick={() => navigate('/resources')}
                  className="w-full btn btn-outline"
                >
                  Upload Resources
                </button>
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full btn btn-outline"
                >
                  View Students
                </button>
                <GoogleCalendarButton />
                <RunReminderTickButton />
              </>
            )}
          </div>
        </div>

        {/* Upcoming Sessions List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Upcoming Sessions</h2>
          </div>
          {!Array.isArray(upcomingBookings) || upcomingBookings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No upcoming sessions.</p>
              {user?.role === 'STUDENT' && (
                <button
                  onClick={() => navigate('/schedule')}
                  className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Book your first lesson to get started!
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingBookings.map((booking) => (
                <div key={booking.id} className={`border rounded-lg p-4 ${
                  isTutorSuggested(booking) && booking.status === 'REQUESTED' 
                    ? 'border-blue-300 bg-blue-50' 
                    : 'border-gray-200'
                }`}>
                  {isTutorSuggested(booking) && booking.status === 'REQUESTED' && user?.role === 'STUDENT' && (
                    <div className="mb-3 p-2 bg-blue-100 border border-blue-200 rounded-md">
                      <p className="text-sm font-medium text-blue-900 mb-1">
                        🎓 Lesson Suggestion from Your Tutor
                      </p>
                      <p className="text-xs text-blue-700 mb-2">
                        Your tutor has suggested this lesson time. Please review and accept or decline.
                      </p>
                      {getTutorMessage(booking) && getTutorMessage(booking) !== 'Lesson suggested by tutor' && (
                        <div className="bg-white dark:bg-gray-700 border border-blue-200 dark:border-blue-600 rounded-md p-2">
                          <p className="text-xs font-medium text-blue-800 mb-1">Message from your tutor:</p>
                          <p className="text-sm text-blue-900 italic">"{getTutorMessage(booking)}"</p>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {booking.title || `${booking.subject.name} - ${booking.isOnline ? 'Online' : 'In Person'}`}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {user?.role === 'STUDENT' 
                          ? `with ${booking.tutor?.name}`
                          : `with ${booking.student?.name}`
                        }
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                      {isTutorSuggested(booking) && booking.status === 'REQUESTED' ? 'SUGGESTED' : booking.status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-600">
                      {formatDateTime(booking.startTime)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {/* Special buttons for tutor-suggested bookings */}
                    {isTutorSuggested(booking) && booking.status === 'REQUESTED' && user?.role === 'STUDENT' ? (
                      <>
                        <button
                          onClick={() => handleAcceptSuggestion(booking.id)}
                          className="flex items-center text-sm text-green-600 hover:text-green-700 bg-green-50 px-3 py-1 rounded-md font-medium"
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Accept Lesson
                        </button>
                        <button
                          onClick={() => handleDeclineSuggestion(booking.id)}
                          className="flex items-center text-sm text-red-600 hover:text-red-700 bg-red-50 px-3 py-1 rounded-md"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Decline
                        </button>
                        <button
                          onClick={() => handleAmendBooking(booking)}
                          className="flex items-center text-sm text-yellow-600 hover:text-yellow-700 bg-yellow-50 px-3 py-1 rounded-md"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Suggest Different Time
                        </button>
                      </>
                    ) : (
                      <>
                        {booking.meetingLink && (
                          <a
                            href={booking.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-sm text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1 rounded-md"
                          >
                            <Video className="w-4 h-4 mr-1" />
                            Teams Link
                          </a>
                        )}
                        <button
                          onClick={() => handleAmendBooking(booking)}
                          className="flex items-center text-sm text-yellow-600 hover:text-yellow-700 bg-yellow-50 px-3 py-1 rounded-md"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Amend
                        </button>
                        <button
                          onClick={() => handleCancelBooking(booking.id)}
                          className="flex items-center text-sm text-red-600 hover:text-red-700 bg-red-50 px-3 py-1 rounded-md"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </button>
                      </>
                    )}
                    {user?.role === 'TUTOR' && booking.status === 'CONFIRMED' && (
                      <>
                        <button
                          onClick={() => handleAttachTeamsLink(booking)}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1 rounded-md"
                        >
                          <LinkIcon className="w-4 h-4 mr-1" />
                          {booking.meetingLink ? 'Update Teams Link' : 'Attach Teams Link'}
                        </button>
                        <button
                          onClick={() => handleAddFeedback(booking)}
                          className="flex items-center text-sm text-purple-600 hover:text-purple-700 bg-purple-50 px-3 py-1 rounded-md"
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Add Feedback
                        </button>
                      </>
                    )}
                    {user?.role === 'STUDENT' && booking.status === 'CONFIRMED' && (
                      <>
                        <button
                          onClick={() => navigate('/payments')}
                          className="flex items-center text-sm text-green-600 hover:text-green-700 bg-green-50 px-3 py-1 rounded-md"
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Pay for Lesson
                        </button>
                        <button
                          onClick={() => navigate('/resources')}
                          className="flex items-center text-sm text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-3 py-1 rounded-md"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Access Resources
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards Section - At the bottom */}
      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="card">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Upcoming Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{Array.isArray(upcomingBookings) ? upcomingBookings.length : 0}</p>
            </div>
          </div>
        </div>

        <div className="card relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-600">My Subjects</p>
                {user?.role === 'STUDENT' ? (
                  <div>
                    {!showSubjectSelector ? (
                      <div 
                        className="cursor-pointer group"
                        onClick={() => setShowSubjectSelector(true)}
                      >
                        {user?.studentProfile?.subjects && user.studentProfile.subjects.length > 0 ? (
                          <div className="mt-1">
                            <p className="text-sm text-gray-900 font-medium">
                              {user.studentProfile.subjects.map(s => s.name).join(', ')}
                            </p>
                            <p className="text-xs text-primary-600 group-hover:underline mt-1">
                              Click to change subjects
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-primary-600 hover:underline mt-1">Click to select subjects</p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2">
                        <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
                          {allSubjects.map((subject) => (
                            <label key={subject.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={selectedSubjectIds.includes(subject.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedSubjectIds([...selectedSubjectIds, subject.id]);
                                  } else {
                                    setSelectedSubjectIds(selectedSubjectIds.filter(id => id !== subject.id));
                                  }
                                }}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm">{subject.name} - {subject.category.replace('_', ' ')}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex space-x-2 mt-2">
                          <button
                            onClick={() => {
                              setShowSubjectSelector(false);
                              setSelectedSubjectIds(user?.studentProfile?.subjects?.map(s => s.id) || []);
                            }}
                            className="text-xs px-2 py-1 text-gray-600 hover:text-gray-800"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={updateSubjects}
                            disabled={savingSubjects}
                            className="text-xs px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                          >
                            {savingSubjects ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">All</p>
                )}
              </div>
            </div>
            {user?.role === 'STUDENT' && !showSubjectSelector && (
              <Edit className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Teams Link Modal */}
      {showTeamsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedBooking?.meetingLink ? 'Update Teams Link' : 'Attach Teams Link'}
              </h3>
              <button
                onClick={handleCloseTeamsModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Session: {selectedBooking?.title || `${selectedBooking?.subject.name} - ${selectedBooking?.isOnline ? 'Online' : 'In Person'}`}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Time: {selectedBooking ? formatDateTime(selectedBooking.startTime) : ''}
              </p>
            </div>

            <div className="mb-6">
              <label htmlFor="teamsLink" className="block text-sm font-medium text-gray-700 mb-2">
                Microsoft Teams Meeting Link
              </label>
              <input
                id="teamsLink"
                type="url"
                value={teamsLink}
                onChange={(e) => setTeamsLink(e.target.value)}
                placeholder="https://teams.microsoft.com/l/meetup-join/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Paste your Microsoft Teams meeting link here. Students will be able to join the session using this link.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSaveTeamsLink}
                disabled={!teamsLink.trim()}
                className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Link
              </button>
              <button
                onClick={handleCloseTeamsModal}
                className="flex-1 btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Amendment Modal */}
      {showAmendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Amend Booking
              </h3>
              <button
                onClick={handleCloseAmendModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Session: {selectedBooking?.title || `${selectedBooking?.subject.name} - ${selectedBooking?.isOnline ? 'Online' : 'In Person'}`}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                With: {selectedBooking?.tutor?.name}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <TimeInput12Hour
                id="startTime"
                label="Start Time"
                value={amendmentData.startTime}
                onChange={(value) => setAmendmentData({...amendmentData, startTime: value})}
              />

              <TimeInput12Hour
                id="endTime"
                label="End Time"
                value={amendmentData.endTime}
                onChange={(value) => setAmendmentData({...amendmentData, endTime: value})}
              />

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Amendment (Optional)
                </label>
                <textarea
                  id="notes"
                  value={amendmentData.notes}
                  onChange={(e) => setAmendmentData({...amendmentData, notes: e.target.value})}
                  placeholder="Please explain why you need to change the booking time..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleAmendSubmit}
                disabled={!amendmentData.startTime || !amendmentData.endTime}
                className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Amendment
              </button>
              <button
                onClick={handleCloseAmendModal}
                className="flex-1 btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Add Lesson Feedback
              </h3>
              <button
                onClick={handleCloseFeedbackModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Session: {selectedBooking?.title || `${selectedBooking?.subject.name} - ${selectedBooking?.isOnline ? 'Online' : 'In Person'}`}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Student: {selectedBooking?.student?.name}
              </p>
            </div>

            <div className="space-y-6 mb-6">
              <div>
                <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                  General Feedback
                </label>
                <textarea
                  id="feedback"
                  value={feedbackData.feedback}
                  onChange={(e) => setFeedbackData({...feedbackData, feedback: e.target.value})}
                  placeholder="Provide general feedback about the lesson, student's performance, and overall progress..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="stepsToImprove" className="block text-sm font-medium text-gray-700 mb-2">
                  Steps to Improve
                </label>
                <textarea
                  id="stepsToImprove"
                  value={feedbackData.stepsToImprove}
                  onChange={(e) => setFeedbackData({...feedbackData, stepsToImprove: e.target.value})}
                  placeholder="Specify areas where the student can improve and actionable steps they can take..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="homeworkTargets" className="block text-sm font-medium text-gray-700 mb-2">
                  Homework Targets
                </label>
                <textarea
                  id="homeworkTargets"
                  value={feedbackData.homeworkTargets}
                  onChange={(e) => setFeedbackData({...feedbackData, homeworkTargets: e.target.value})}
                  placeholder="Explain what the assigned homework aims to achieve and how it will help the student..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleFeedbackSubmit}
                disabled={!feedbackData.feedback || !feedbackData.stepsToImprove || !feedbackData.homeworkTargets}
                className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Feedback
              </button>
              <button
                onClick={handleCloseFeedbackModal}
                className="flex-1 btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notice Display Component - Only for Students */}
      {user?.role === 'STUDENT' && <NoticeDisplay />}
    </div>
  );
};

export default DashboardPage; 